/**
 * Selection Slice
 *
 * Manages user selection state (location, stop, date, timeslot).
 */

import type { Location, Stop } from '@/types/location';

export interface SelectionSlice {
  selectedLocationId: number | null;
  selectedStopId: number | null;
  selectedDate: string | null;
  selectedTimeslotId: string | null;

  // Actions
  selectLocation: (locationId: number | null) => void;
  selectStop: (stopId: number | null) => void;
  selectDate: (date: string | null) => void;
  selectTimeslot: (timeslotId: string | null) => void;
  clearSelection: () => void;
  clearDateSelection: () => void;

  // Computed
  getSelectedLocation: () => Location | null;
  getSelectedStop: () => Stop | null;
}

export const createSelectionSlice = (set: any, get: any): SelectionSlice => ({
  // Initial state
  selectedLocationId: null,
  selectedStopId: null,
  selectedDate: null,
  selectedTimeslotId: null,

  /**
   * Select a location on the map
   *
   * @param locationId - Location ID to select (null to clear)
   */
  selectLocation: (locationId) => {
    set({
      selectedLocationId: locationId,
      selectedStopId: null, // Reset stop selection when location changes
    });
  },

  /**
   * Select a stop for the current location
   *
   * @param stopId - Stop ID to select (null to clear)
   */
  selectStop: (stopId) => {
    set({ selectedStopId: stopId });
  },

  /**
   * Select a date (used by calendar in location-details)
   *
   * @param date - Date string to select (null to clear)
   */
  selectDate: (date) => {
    set({ selectedDate: date });
  },

  /**
   * Select a timeslot for the current date
   *
   * @param timeslotId - Timeslot ID to select (null to clear)
   */
  selectTimeslot: (timeslotId) => {
    set({ selectedTimeslotId: timeslotId });
  },

  /**
   * Clear current selection
   */
  clearSelection: () => {
    set({
      selectedLocationId: null,
      selectedStopId: null,
      selectedDate: null,
      selectedTimeslotId: null,
    });
  },

  /**
   * Clear date and timeslot selection only
   */
  clearDateSelection: () => {
    set({
      selectedDate: null,
      selectedTimeslotId: null,
    });
  },

  /**
   * Computed function: Get selected location
   */
  getSelectedLocation: () => {
    const state = get();
    if (!state) return null;

    const { selectedLocationId } = state;
    if (!selectedLocationId) return null;

    // Access cross-slice data via get()
    const fullState = get() as any; // Cast to access getAllLocations from location-data slice
    if (!fullState?.getAllLocations) return null;

    const allLocations = fullState.getAllLocations();
    return allLocations.find((loc: Location) => loc.id === selectedLocationId) ?? null;
  },

  /**
   * Computed function: Get selected stop
   */
  getSelectedStop: () => {
    const state = get();
    if (!state) return null;

    const { selectedLocationId, selectedStopId } = state;
    if (!selectedLocationId || !selectedStopId) return null;

    const fullState = get();
    if (!fullState?.getSelectedLocation) return null;

    const selectedLocation = fullState.getSelectedLocation();
    if (!selectedLocation) return null;

    return (
      selectedLocation.stops.find((stop: Stop) => stop.id === selectedStopId) ?? null
    );
  },
});
